/**
 * Vue Router 4 Configuration
 *
 * @version 2.0.0-alpha.4
 * @description Router setup for Vue 3 with lazy-loaded routes
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

// =============================================================================
// Route Meta Types
// =============================================================================

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    guest?: boolean
    fullscreenPage?: boolean
    roles?: string[]
    /**
     * R1 — la rotta è accessibile solo se l'utente ha ALMENO UNO di questi
     * permessi (authStore.canAny); altrimenti redirect alla dashboard.
     * Guard in main.ts, speculare al filtro voci di menu in useNavigationStore.
     */
    permissionsAny?: string[]
    /**
     * Route di gestione HORECA-FOOD (marchi/categorie/prodotti custom).
     * La guard verifica via BE (useHorecaFoodStore.fetchCanManage) che
     * l'utente sia admin/super-admin oppure agente abilitato MKT 6;
     * altrimenti redirect alla dashboard.
     */
    requiresHorecaFoodManage?: boolean
  }
}

// =============================================================================
// Routes Definition
// =============================================================================

const routes: RouteRecordRaw[] = [
  // -------------------------------------------------------------------------
  // Dashboard
  // -------------------------------------------------------------------------
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/views/Dashboard.vue'),
    meta: {
      requiresAuth: true,
    },
  },

  // -------------------------------------------------------------------------
  // Auth Routes (Guest only)
  // -------------------------------------------------------------------------
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
    meta: {
      guest: true,
      fullscreenPage: true,
    },
  },
  {
    path: '/forgot',
    name: 'forgot',
    component: () => import('@/views/PasswordForgot.vue'),
    meta: {
      guest: true,
      fullscreenPage: true,
    },
  },

  // -------------------------------------------------------------------------
  // Admin Routes
  // -------------------------------------------------------------------------
  {
    path: '/users',
    name: 'users',
    component: () => import('@/views/Admin/Users.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/users/:id',
    name: 'user-details',
    component: () => import('@/views/Admin/UserDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/roles',
    name: 'roles',
    component: () => import('@/views/Admin/RolesPermissions.vue'),
    meta: {
      requiresAuth: true,
      permissionsAny: ['roles.view', 'roles.manage'],
    },
  },
  {
    path: '/notification-events',
    name: 'notification-events',
    component: () => import('@/views/Admin/NotificationEvents.vue'),
    meta: {
      requiresAuth: true,
      permissionsAny: ['notifications.rules.manage'],
    },
  },
  {
    path: '/pdf-templates',
    name: 'pdf-templates',
    component: () => import('@/views/Admin/PdfTemplates.vue'),
    meta: {
      requiresAuth: true,
      permissionsAny: ['pdf.templates.manage'],
    },
  },
  {
    path: '/sectors',
    name: 'sectors',
    component: () => import('@/views/Admin/Sectors.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/reports/agents-activity',
    name: 'agents-activity',
    component: () => import('@/views/Admin/ReportAgentsActivity.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/associations/custom',
    name: 'associations-custom',
    component: () => import('@/views/Admin/AssociationsCustom.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/associations/associations-provinces',
    name: 'associations-provinces',
    component: () => import('@/views/Admin/AssociationsProvinces.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/standard-quotes',
    name: 'standard-quotes',
    component: () => import('@/views/Admin/StandardQuotes.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/standard-quotes/:id',
    name: 'standard-quote-details',
    component: () => import('@/views/Admin/StandardQuoteDetails.vue'),
    props: (route) => ({
      id: Number(route.params.id),
    }),
    meta: {
      requiresAuth: true,
    },
  },

  // -------------------------------------------------------------------------
  // AR Catalog Routes (Configuratore prodotti AR)
  // -------------------------------------------------------------------------
  {
    path: '/ar/products',
    name: 'ar-products',
    component: () => import('@/views/Admin/ArProducts.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    // NB: la rotta statica /new DEVE precedere la dinamica /:id, altrimenti
    // 'new' verrebbe catturato come :id. Niente props -> id undefined -> create.
    path: '/ar/products/new',
    name: 'ar-product-create',
    component: () => import('@/views/Admin/ArProductDetails.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/ar/products/:id',
    name: 'ar-product-details',
    component: () => import('@/views/Admin/ArProductDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/ar/product-groups',
    name: 'ar-product-groups',
    component: () => import('@/views/Admin/ArProductGroups.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/ar/wall-claddings',
    name: 'ar-wall-claddings',
    component: () => import('@/views/Admin/WallCladdings.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/ar/combinations',
    name: 'ar-combinations',
    component: () => import('@/views/Admin/ArCombinations.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    // NB: la rotta statica /new DEVE precedere la dinamica /:id, altrimenti
    // 'new' verrebbe catturato come :id. Niente props -> id undefined -> create.
    path: '/ar/combinations/new',
    name: 'ar-combination-create',
    component: () => import('@/views/Admin/ArCombinationDetails.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/ar/combinations/:id',
    name: 'ar-combination-details',
    component: () => import('@/views/Admin/ArCombinationDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },

  // -------------------------------------------------------------------------
  // Commercial Routes
  // -------------------------------------------------------------------------
  {
    // Progetti AR: sotto /commercial/* perché li usano i commerciali di zona
    // (la guard confina il ruolo commercial a /commercial/*); gli admin li
    // vedono comunque. Endpoint BE: /v2/admin/projects (gated admin+commercial).
    path: '/commercial/ar-projects',
    name: 'ar-projects',
    component: () => import('@/views/Commercial/ArProjects.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/ar-projects/:id',
    name: 'ar-project-details',
    component: () => import('@/views/Commercial/ArProjectDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/agenda',
    name: 'agenda',
    component: () => import('@/views/Commercial/Agenda.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/products',
    name: 'products',
    component: () => import('@/views/Commercial/Products.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/products/brands',
    name: 'horeca-brands',
    component: () => import('@/views/Commercial/HorecaBrands.vue'),
    meta: {
      requiresAuth: true,
      requiresHorecaFoodManage: true,
    },
  },
  {
    path: '/commercial/products/categories',
    name: 'horeca-categories',
    component: () => import('@/views/Commercial/HorecaCategories.vue'),
    meta: {
      requiresAuth: true,
      requiresHorecaFoodManage: true,
    },
  },
  {
    path: '/commercial/products/:code',
    name: 'product-details',
    component: () => import('@/views/Commercial/ProductDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/contacts/customers',
    name: 'customers',
    component: () => import('@/views/Commercial/Customers.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/contacts/customers/:code',
    name: 'customer-details',
    component: () => import('@/views/Commercial/CustomerDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/contacts/prospects',
    name: 'prospects',
    component: () => import('@/views/Commercial/Prospects.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/contacts/prospects/:id',
    name: 'prospect-details',
    component: () => import('@/views/Commercial/ProspectDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/contacts/censuses',
    name: 'censuses',
    component: () => import('@/views/Commercial/Censuses.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/contacts/censuses/:id',
    name: 'census-details',
    component: () => import('@/views/Commercial/CensusDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/quotes',
    name: 'quotes',
    component: () => import('@/views/Commercial/Quotes.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/quotes/new',
    name: 'new-quote',
    component: () => import('@/views/Commercial/QuoteNew.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/quotes/:type/:year/:number/:rev/new',
    name: 'new-quote-rev',
    component: () => import('@/views/Commercial/QuoteNew.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/quotes/:type/:year/:number/:rev',
    name: 'quote-details-by-rev',
    component: () => import('@/views/Commercial/QuoteDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/commercial/quotes/:id',
    name: 'quote-details-by-id',
    component: () => import('@/views/Commercial/QuoteDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    // R2 — Commesse: voce in ENTRAMBE le aree (qui il path commercial).
    // Gating = permessi di vista (lo scope object-level lo applica il BE).
    path: '/commercial/commesse',
    name: 'commesse',
    component: () => import('@/views/Commesse/Commesse.vue'),
    meta: {
      requiresAuth: true,
      permissionsAny: ['commesse.view.all', 'commesse.view.macroarea'],
    },
  },
  {
    path: '/commercial/commesse/:id',
    name: 'commessa-details',
    component: () => import('@/views/Commesse/CommessaDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      permissionsAny: ['commesse.view.all', 'commesse.view.macroarea'],
    },
  },

  // -------------------------------------------------------------------------
  // Tech Routes
  // -------------------------------------------------------------------------
  {
    // R2 — Commesse lato area tecnica (stesso componente del commercial)
    path: '/tech/commesse',
    name: 'tech-commesse',
    component: () => import('@/views/Commesse/Commesse.vue'),
    meta: {
      requiresAuth: true,
      permissionsAny: ['commesse.view.all', 'commesse.view.macroarea'],
    },
  },
  {
    path: '/tech/commesse/:id',
    name: 'tech-commessa-details',
    component: () => import('@/views/Commesse/CommessaDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      permissionsAny: ['commesse.view.all', 'commesse.view.macroarea'],
    },
  },
  {
    path: '/tech/technicians/cars',
    name: 'technicians-cars',
    component: () => import('@/views/Tech/TechniciansCars.vue'),
    meta: {
      requiresAuth: true,
      permissionsAny: ['technicians.manage'],
    },
  },
  {
    path: '/tech/technicians/data',
    name: 'technicians-data',
    component: () => import('@/views/Tech/Technicians.vue'),
    meta: {
      requiresAuth: true,
      permissionsAny: ['technicians.manage'],
    },
  },
  {
    path: '/tech/technicians/:id',
    name: 'technician-details',
    component: () => import('@/views/Tech/TechnicianDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      permissionsAny: ['technicians.manage'],
    },
  },
  {
    path: '/tech/machines/data',
    name: 'machines-data',
    component: () => import('@/views/Tech/MachinesData.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/machines/manufacturers',
    name: 'machines-manufacturers',
    component: () => import('@/views/Tech/MachinesManufacturers.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/machines/models',
    name: 'machines-models',
    component: () => import('@/views/Tech/MachinesModels.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/machines/operations',
    name: 'machines-operations',
    component: () => import('@/views/Tech/MachinesOperations.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/machines/problems',
    name: 'machines-problems',
    component: () => import('@/views/Tech/MachinesProblems.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/machines/:id',
    name: 'machine-details',
    component: () => import('@/views/Tech/MachineDataDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/cst-interventions',
    name: 'cst-interventions',
    component: () => import('@/views/Tech/CstInterventions.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/cst-interventions/:id',
    name: 'cst-intervention-details',
    component: () => import('@/views/Tech/CstInterventionsDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/interventions',
    redirect: { name: 'interventions' },
  },
  {
    path: '/tech/interventions/data',
    name: 'interventions',
    component: () => import('@/views/Tech/Interventions.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/interventions/dashboard',
    name: 'interventions-dashboard',
    component: () => import('@/views/Tech/InterventionsDashboard.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      fullscreenPage: true,
    },
  },
  {
    path: '/tech/interventions/data/:id',
    name: 'intervention-details',
    component: () => import('@/views/Tech/InterventionsDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/interventions/materials',
    name: 'intervention-materials',
    component: () => import('@/views/Tech/InterventionsMaterials.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/interventions/transfers',
    name: 'intervention-transfers',
    component: () => import('@/views/Tech/InterventionsTransfers.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/interventions/workinghours',
    name: 'intervention-workinghours',
    component: () => import('@/views/Tech/InterventionsWorkinghours.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/products',
    name: 'tech-products',
    component: () => import('@/views/Commercial/Products.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/products/:code',
    name: 'tech-product-details',
    component: () => import('@/views/Commercial/ProductDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/customers',
    name: 'tech-customers',
    component: () => import('@/views/Commercial/Customers.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/customers/:code',
    name: 'tech-customer-details',
    component: () => import('@/views/Commercial/CustomerDetails.vue'),
    props: true,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/tech/reports/export',
    name: 'tech-reports-export',
    component: () => import('@/views/Tech/ReportTechExport.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      permissionsAny: ['reports.tech.view'],
    },
  },
  {
    path: '/tech/reports/working-hours',
    name: 'tech-reports-working-hours',
    component: () => import('@/views/Tech/ReportTechHours.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      permissionsAny: ['reports.tech.view'],
    },
  },
  {
    path: '/tech/reports/occupation',
    name: 'tech-reports-occupation',
    component: () => import('@/views/Tech/ReportTechOccupation.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      permissionsAny: ['reports.tech.occupation'],
    },
  },
  {
    path: '/tech/reports/costs',
    name: 'tech-reports-costs',
    component: () => import('@/views/Tech/ReportTechCosts.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      permissionsAny: ['reports.tech.view'],
    },
  },

  // -------------------------------------------------------------------------
  // Catch-all 404 (Vue Router 4 syntax)
  // -------------------------------------------------------------------------
  {
    path: '/:pathMatch(.*)*',
    name: '404',
    component: () => import('@/views/Page404.vue'),
  },
]

// =============================================================================
// Create Router Instance
// =============================================================================

const router = createRouter({
  history: createWebHistory('/'),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // Don't scroll to top when only query params change (e.g. URL sync, load more)
    if (_to.path === _from.path) {
      return false
    }
    return { top: 0 }
  },
})

// =============================================================================
// Chunk Load Error Recovery
// =============================================================================
// After a new deploy, old JS chunks no longer exist on the server.
// If the browser still has a stale index.html referencing old hashes,
// lazy-loaded routes will fail with a MIME type error (server returns HTML
// fallback instead of JS). We catch this and force a full page reload
// so the browser fetches the new index.html with correct chunk hashes.
// A sessionStorage flag prevents infinite reload loops.
// =============================================================================

const RELOAD_FLAG = '__chunk_reload__'

router.onError((error, to) => {
  const isChunkError =
    error.message?.includes('Failed to fetch dynamically imported module') ||
    error.message?.includes('Importing a module script failed') ||
    error.message?.includes('error loading dynamically imported module')

  if (isChunkError) {
    const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG)

    if (!alreadyReloaded) {
      console.warn(`[Router] Chunk load failed for "${to.fullPath}", reloading...`)
      sessionStorage.setItem(RELOAD_FLAG, '1')
      window.location.assign(to.fullPath)
    } else {
      console.error('[Router] Chunk load failed after reload, clearing SW caches...')
      sessionStorage.removeItem(RELOAD_FLAG)
      // Nuclear option: clear all caches and reload
      if (typeof caches !== 'undefined') {
        caches.keys().then((names) =>
          Promise.all(names.map((n) => caches.delete(n)))
        ).then(() => window.location.reload())
      }
    }
  }
})

// Clear the reload flag on successful navigation
router.afterEach(() => {
  sessionStorage.removeItem(RELOAD_FLAG)
})

// =============================================================================
// Export
// =============================================================================

export default router
